<script setup>
  import {computed, inject, onMounted, ref, watch} from "vue";

  import ComponentIcon from "@components/Icons/Icon.vue";
  import {Deferred as InertiaDeferred, router, usePage} from "@inertiajs/vue3";
  import DiseasesTableRow from "@pages/Records/Components/Sections/DiseaseBar/Components/Row.vue";
  import DiseaseSkeletonRow from "@pages/Records/Components/Sections/DiseaseBar/Components/SkeletonRow.vue";
  import {MedButton, MedCheckbox} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {delayWhileEditing} from "@utils/Helpers/DelayWhileEditing.js";
  import {sortList} from "@utils/Helpers/ListSorter.js";
  import {cloneDeep, debounce, isEqual} from "es-toolkit";
  import Sortable from "sortablejs";

  defineOptions({
    name: "DiseasesTable",
  });

  const props = defineProps({
    isEditable: {
      type: Boolean,
      required: true,
    },
    recordData: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      default: null,
    },
  });

  const page = usePage();

  const setIsMassUpdateProcessing = inject("setIsMassUpdateProcessing");
  const deleteDialog = inject("recordEditDeleteRowDialog");

  const refDraggable = ref(null);

  const isDragging = ref(false);
  const diseases = ref([...props.recordData.diseases]);
  const pendingSortOrder = ref(null);
  const sortable = ref(null);
  const sortCancelToken = ref(null);
  const selectedRows = ref([]);

  const getDiseaseIds = (items) => items.map((disease) => disease.id);

  // a disease may only carry an ICD-C name, matching how the row displays it
  const getDiseaseName = (disease) => disease.disease ?? disease.name_icdc;

  onMounted(() => {
    initSortable();
  });

  const initSortable = () => {
    if (sortable.value !== null) {
      return; // already initialized
    }

    if (refDraggable.value === null) {
      return; // refDraggable is not available yet
    }

    sortable.value = Sortable.create(refDraggable.value, {
      handle: "[drag-handle]",
      draggable: "[drag-item]",
      direction: "vertical",
      ghostClass: "sortable-ghost",
      dataIdAttr: "drag-item", // requires `drag-item="{{item.id}}"`
      store: {
        /**
         * Save the order of elements. Called onEnd (when the item is dropped).
         * @param {Sortable} sortable
         */
        set: (sortable) => {
          // sort medications according to new sort order
          updateOrder(sortable.toArray());
        },
      },
      onStart: () => {
        isDragging.value = true;
      },
      onEnd: () => {
        isDragging.value = false;
      },
    });
  };

  const sortDirection = computed(() => {
    if (diseases.value?.length > 0) {
      const copyOfDiseases = cloneDeep(diseases.value);

      const diseaseIds = copyOfDiseases.map((disease) => disease.id);
      const ascSortedObject = sortList(copyOfDiseases, getDiseaseName, "asc");

      if (isEqual(diseaseIds, ascSortedObject.sortedIds)) {
        return "asc";
      }

      // avoid calling mutating reverse() method on original array
      if (isEqual(diseaseIds, ascSortedObject.sortedIds.toReversed())) {
        return "desc";
      }
    }

    return "";
  });

  const newSortDirection = computed(() => {
    if (sortDirection.value === "" || sortDirection.value === "desc") {
      return "asc";
    }

    if (sortDirection.value === "asc") {
      return "desc";
    }

    return "";
  });

  watch(
    () => props.recordData.diseases,
    (newDiseases) => {
      const incomingSortOrder = getDiseaseIds(newDiseases);

      if (
        pendingSortOrder.value !== null &&
        newDiseases.length === diseases.value.length &&
        !isEqual(incomingSortOrder, pendingSortOrder.value)
      ) {
        return;
      }

      diseases.value = [...newDiseases];

      if (pendingSortOrder.value !== null && isEqual(incomingSortOrder, pendingSortOrder.value)) {
        pendingSortOrder.value = null;
      }
    },
  );

  watch(
    () => diseases.value,
    (value, oldValue) => {
      if (value.length !== oldValue?.length) {
        initSortable();
      }

      // remove deleted ids from selectedRows
      const newIds = new Set(value.map((disease) => disease.id));
      selectedRows.value = selectedRows.value.filter((id) => newIds.has(id));
    },
  );

  const updateDiseases = () => {
    setIsMassUpdateProcessing("disease", true);
    putDiseases();
  };

  const putDiseases = debounce(
    () => {
      delayWhileEditing(refDraggable, () => {
        router.patch(
          route("diseases.mass-update", {
            patient: page.props.patient.id,
            record: page.props.record.id,
          }),
          {
            ...diseases.value,
          },
          {
            preserveScroll: true,
            only: ["flash", "errors", "diseases"],
            onFinish: () => {
              setIsMassUpdateProcessing("disease", false);
            },
          },
        );
      });
    },
    500,
    {trailing: true},
  );

  const updateOrder = (newSortOrder) => {
    const currentSortOrder = getDiseaseIds(diseases.value);

    if (isEqual(currentSortOrder, newSortOrder)) {
      return;
    }

    pendingSortOrder.value = [...newSortOrder];

    diseases.value = diseases.value.toSorted((a, b) => {
      return newSortOrder.indexOf(a.id) - newSortOrder.indexOf(b.id);
    });

    if (sortCancelToken.value) {
      sortCancelToken.value.cancel();
    }

    // lock immediately (not inside the debounced request) so adding/removing a
    // disease can't race with the pending sort request and desync the ids snapshot
    setIsMassUpdateProcessing("disease", true);

    updateOrderRequest(newSortOrder);
  };

  const updateOrderRequest = debounce((newSortOrder) => {
    router.post(
      route("sort.store", {
        patient: page.props.patient.id,
        record: page.props.record.id,
        relation: "diseases",
      }),
      {
        ids: newSortOrder,
      },
      {
        preserveScroll: true,
        only: ["flash", "diseases"],
        onFinish: () => {
          setIsMassUpdateProcessing("disease", false);
        },
        onCancelToken: (cancelToken) => (sortCancelToken.value = cancelToken),
      },
    );
  }, 2000);

  const sortByName = () => {
    if (!props.isEditable) return;

    const sortedObject = sortList(diseases.value, getDiseaseName, newSortDirection.value);

    updateOrder(sortedObject.sortedIds);
  };

  const showDeleteConfirmDialog = () => {
    deleteDialog({routeName: "diseases", payload: {disease: selectedRows.value}});
  };

  const selectAllState = computed({
    get: () => {
      if (selectedRows.value.length === diseases.value.length) return true;
      if (selectedRows.value.length > 0) return "indeterminate";
      return false;
    },
    set: (value) => {
      selectedRows.value = value ? diseases.value.map((d) => d.id) : [];
    },
  });

  const toggleRow = (id) => {
    const index = selectedRows.value.indexOf(id);
    if (index !== -1) {
      selectedRows.value.splice(index, 1);
    } else {
      selectedRows.value.push(id);
    }
  };
</script>

<template>
  <InertiaDeferred data="diseases">
    <template #fallback />

    <template v-if="diseases.length > 0">
      <!-- ☑️ multiselect actions -->
      <section v-if="mode === 'multi-select' && selectedRows?.length" class="items-top mb-2 flex space-x-1">
        <div class="my-2 rounded border-2 border-transparent bg-gray-200 px-1 py-0 text-xs font-semibold text-gray-700">
          {{ selectedRows?.length }}
        </div>

        <div class="flex flex-wrap gap-0.5">
          <MedButton
            flat
            icon="delete"
            :data-test="getTestId('default-head-bulk-delete-button')"
            @click="showDeleteConfirmDialog()"
          >
            Löschen
          </MedButton>
        </div>
      </section>

      <div class="grid grid-cols-12 items-end gap-x-6 pb-2 text-sm font-normal text-gray-700">
        <div class="col-span-8 flex items-center space-x-1">
          <template v-if="isEditable">
            <div class="flex size-6 shrink-0 items-center justify-center stroke-0">
              <MedCheckbox
                v-if="mode === 'multi-select'"
                v-model="selectAllState"
                aria-label="Alle Zeilen auswählen"
                variant="primary"
                :data-test="getTestId('table-select-all-checkbox')"
              />
            </div>

            <button :class="cn('ml-2 flex items-center text-gray-700 hover:no-underline')" @click.prevent="sortByName">
              <template v-if="sortDirection === 'asc'">
                <ComponentIcon>expand_more</ComponentIcon>
              </template>

              <template v-else-if="sortDirection === 'desc'">
                <ComponentIcon>expand_less</ComponentIcon>
              </template>

              <template v-else>
                <ComponentIcon>unfold_more</ComponentIcon>
              </template>

              <span>Erkrankung / ICD-10 / Allergie / Lebensumstand</span>
            </button>
          </template>
          <template v-else>
            <span class="ml-2">Erkrankung / ICD-10 / Allergie / Lebensumstand</span>
          </template>
        </div>

        <div class="col-span-3">Seit (Datum; opt.)</div>
        <div class="col-span-1" />
      </div>
    </template>
  </InertiaDeferred>

  <ul ref="refDraggable">
    <InertiaDeferred data="diseases">
      <template #fallback>
        <template v-for="index in 5" :key="index">
          <DiseaseSkeletonRow :test-id="getTestId(`table-${index}`)" />
        </template>
      </template>

      <template v-for="(disease, index) in diseases" :key="disease.id">
        <DiseasesTableRow
          :drag-item="disease.id"
          :is-editable="isEditable"
          :disease="disease"
          :is-dragging="isDragging"
          :test-id="getTestId(`table-${index}`)"
          @update-diseases="updateDiseases"
        >
          <template v-if="mode === 'multi-select'" #prefix>
            <div class="flex size-6 shrink-0 items-center justify-center stroke-0">
              <MedCheckbox
                :model-value="selectedRows.includes(disease.id)"
                :aria-label="`Zeile ${disease.disease} auswählen`"
                variant="primary"
                :data-test="getTestId('table-row-select-checkbox')"
                @update:model-value="toggleRow(disease.id)"
              />
            </div>
          </template>
        </DiseasesTableRow>
      </template>
    </InertiaDeferred>
  </ul>
</template>
